/* eslint-disable react/jsx-curly-brace-presence */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { LEARNING_ASSET_TYPES } from '../../Constants';
import VerbHeading from '../../components/VerbHeading';
import VerbButton, { VERB_BUTTON_TYPE } from '../../components/VerbButton';

const CollectionSubSection = ({ title, children, showDivider = false, isLockedReflection = false }) => (
    <div>
        <VerbHeading hNumber={1} messageId={title} defaultMessage={title} />
        <div
            style={{ marginLeft: '2%', ...(isLockedReflection ? { paddingBottom: 0 } : {}) }}
            className={showDivider ? 'show-divider' : ''}
        >
            {children}
        </div>
    </div>
);

CollectionSubSection.propTypes = {
    title: PropTypes.string,
    children: PropTypes.object,
    showDivider: PropTypes.bool,
    isLockedReflection: PropTypes.bool
};

const ReflectionPrompt = ({ text, subText, assetId, htmlText = null, hideNoButton = false }) => (
    <div className={'col-md-10 col-sm-6 col-10 offset-sm-2 offset-md-1 reflection-prompt'}>
        {htmlText || <VerbHeading hNumber={4} messageId={text} defaultMessage={text} />}
        {typeof subText === 'string' ? <p>{subText}</p> : subText}
        <div className={'col-5 prompt-buttons'}>
            <Link to={`/workspace/${assetId}?path=yesRole`}>
                <VerbButton className={'col-5'} buttonText={'YES'} buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT} heading6BtnText={true} />
            </Link>
            {!hideNoButton && (
                <Link to={`/workspace/${assetId}?path=noRole`}>
                    <VerbButton className={'col-5 h-100'} buttonText={'NO'} buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT} />
                </Link>
            )}
        </div>
    </div>
);

ReflectionPrompt.propTypes = {
    text: PropTypes.string,
    subText: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    assetId: PropTypes.string,
    htmlText: PropTypes.object,
    hideNoButton: PropTypes.bool
};

const RenderRow = ({
    filter,
    rowRenderer,
    asset,
    index,
    children,
    isLockedReflection = false,
    hideDivider = false,
    isReflection = false,
    status = ''
}) => {
    if (filter) {
        return (
            <>
                {rowRenderer(asset, index, isLockedReflection, hideDivider, isReflection, status)}
                {children}
            </>
        );
    }
    return null;
};

RenderRow.propTypes = {
    filter: PropTypes.bool,
    rowRenderer: PropTypes.func,
    asset: PropTypes.object,
    children: PropTypes.object,
    isLockedReflection: PropTypes.bool,
    index: PropTypes.number,
    hideDivider: PropTypes.bool,
    isReflection: PropTypes.bool,
    status: PropTypes.string
};

const CollectionJourneySection = ({ learningAssets, isPracticalApplication = false, rowRenderer = () => {} }) => {
    const [takeActionCompleted, setTakeActionCompleted] = useState(true);
    const [takeActionCompletionDate, setTakeActionCompletionDate] = useState(null);
    const [reflectionActivityStarted, setReflectionActivityStarted] = useState(false);
    const [startSlide, setStartSlide] = useState(0);
    const [takeActionUserLearningAssetId, setTakeActionUserLearningAssetId] = useState('');

    useEffect(() => {
        learningAssets.forEach((asset) => {
            if (asset.type === LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION) {
                if (asset.isCompleted || asset.status === 'COMPLETED') {
                    setTakeActionCompletionDate(asset.completedDateTime);
                    setTakeActionUserLearningAssetId(asset._id);
                    setStartSlide(asset.track?.sections[0]?.sectionItems.length - 2);
                } else {
                    setTakeActionCompleted(false);
                }
            }
            if (
                asset.type === LEARNING_ASSET_TYPES.REFLECTION &&
                (asset.isCompleted ||
                    asset.status === 'COMPLETED' ||
                    asset.status === 'IN_PROGRESS' ||
                    asset.completeness > 0)
            ) {
                setReflectionActivityStarted(true);
            }
        });
    }, [learningAssets]);

    const fourteenDays = moment(
        moment.utc(takeActionCompletionDate).startOf('day').add(14, 'days').format('LL')
    ).startOf('day');

    const fifteenDays = moment(
        moment.utc(takeActionCompletionDate).startOf('day').add(15, 'days').format('LL')
    ).startOf('day');

    const dueDate = fourteenDays.format('LL').split(',')[0];
    const today = new Date(Date.now());

    if (isPracticalApplication) {
        return (
            <div>
                <CollectionSubSection>
                    {learningAssets.map((asset, index) => (
                        <RenderRow
                            filter={
                                ![LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION, LEARNING_ASSET_TYPES.REFLECTION].includes(
                                    asset.type
                                )
                            }
                            asset={asset}
                            index={index}
                            rowRenderer={rowRenderer}
                            status={asset.status}
                        />
                    ))}
                </CollectionSubSection>
                <CollectionSubSection>
                    {learningAssets.map((asset, index) => (
                        <RenderRow
                            filter={LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION === asset.type}
                            asset={asset}
                            index={index}
                            rowRenderer={rowRenderer}
                            status={asset.status}
                        />
                    ))}
                </CollectionSubSection>
                <CollectionSubSection showDivider isLockedReflection={!takeActionCompleted}>
                    {learningAssets.map((asset, index) => (
                        <RenderRow
                            filter={LEARNING_ASSET_TYPES.REFLECTION === asset.type}
                            asset={asset}
                            index={index}
                            rowRenderer={rowRenderer}
                            isLockedReflection={!takeActionCompleted}
                            hideDivider
                            status={asset.status}
                            isReflection
                        >
                            {takeActionCompleted && !reflectionActivityStarted && today < fourteenDays.toDate() && (
                                <ReflectionPrompt
                                    htmlText={
                                        <p>
                                            You committed to take a{' '}
                                            <Link
                                                to={`/workspace/${takeActionUserLearningAssetId}?startSlide=${startSlide}`}
                                            >
                                                small action
                                            </Link>{' '}
                                            by {dueDate}
                                        </p>
                                    }
                                    subText={
                                        <>
                                            <p>
                                                Not done? Try your action this week.
                                            </p>
                                            <p className={'mt-2 mb-2'} style={{fontWeight: 600}}>Did you do your action?</p>
                                        </>
                                    }
                                    assetId={asset._id}
                                    hideNoButton
                                />
                            )}
                            {takeActionCompleted && !reflectionActivityStarted && (today > fifteenDays.toDate() || today > fourteenDays.toDate() &&
                                today < fifteenDays.toDate()) && (
                                <ReflectionPrompt
                                    htmlText={
                                        <h4>
                                            You committed to take a{' '}
                                            <Link
                                                to={`/workspace/${takeActionUserLearningAssetId}?startSlide=${startSlide}`}
                                            >
                                                small action
                                            </Link>{' '}
                                            by {dueDate}.
                                        </h4>
                                    }
                                    subText={
                                        <>
                                            <p>
                                                Whether you did it or not, continue this Path now.
                                            </p>
                                            <p style={{fontWeight: 600, marginTop: 16}}>Did you do your action?</p>
                                        </>
                                    }
                                    assetId={asset._id}
                                />
                            )}
                        </RenderRow>
                    ))}
                </CollectionSubSection>
            </div>
        );
    }
    return learningAssets.map((asset, index) => rowRenderer(asset, index, !takeActionCompleted));
};

export default CollectionJourneySection;
